import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  ActionableNotification,
  Button,
  Column,
  DatePicker,
  DatePickerInput,
  Grid,
  Heading,
  Loading,
  Pagination,
  Search,
  Section,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from "@carbon/react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  displayDateToIso,
  getFromOpenElisServer,
  parseIsoDate,
  postToOpenElisServerJsonResponse,
} from "../../utils/Utils";
import { requestFailed, serverMessage } from "../../utils/requestOutcome";
import ResultAlertModal, {
  acknowledgementRefusal,
  ResultAlert,
} from "../ResultAlertModal";
import { owedResultAlerts } from "./resultAlerts";
import {
  serverPageArrowsProps,
  serverPageSizeOf,
  serverPaginationProps,
} from "../../utils/serverPaging";
import ServerPageArrows from "../../common/ServerPageArrows";
import { ConfigurationContext, NotificationContext } from "../../layout/Layout";
import {
  AlertDialog,
  NotificationKinds,
} from "../../common/CustomNotification";
import ESignatureButton, {
  SignatureMeaning,
} from "../../esignature/ESignatureButton";
import PolymorphicResultCell, {
  ResultCellRow,
  worklistRowKey,
} from "./PolymorphicResultCell";
import {
  RowEditState,
  initialRowState,
  isModifyingSavedResult,
  writesResultValue,
  isRowEditable,
  nextRowState,
  showEdit,
  showSave,
} from "./editState";
import {
  ResultsDomain,
  formatDomainMessage,
  normalizeDomain,
} from "./domainIntl";
import { usePresence } from "./usePresence";
import ExpandedPanel, {
  DilutionDraft,
  IdValue,
  NoteDraft,
  PanelRow,
  RejectDraft,
} from "./ExpandedPanel";
import { ReferralDraft, datePlaceholderId } from "./ReferralAction";
import { NceDisposition, dispositionRequests } from "./nceDisposition";
import { SectionLayout, loadSectionLayout } from "./sectionLayout";
import { FlagChip, accentClass } from "./flags";
import { resultFlagFor } from "./resultFlagFor";
import Avatar from "./Avatar";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import PageBreadCrumb from "../../common/PageBreadCrumb";
import config from "../../../config.json";
import SearchPatientForm from "../../patient/SearchPatientForm";
import { PatientRecord } from "../../patient/types";
import "./unified-results.scss";
import { displayRange, rangeNotAppliedKey } from "../../common/rangeNotApplied";

const replaceResultsUrl = (urlState: URLSearchParams) => {
  const query = urlState.toString();
  window.history.replaceState(
    null,
    "",
    query ? `/Results?${query}` : "/Results",
  );
};

const patientDisplayName = (patient: PatientRecord) =>
  [patient.firstName, patient.lastName].filter(Boolean).join(" ") +
  (patient.subjectNumber ? ` (${patient.subjectNumber})` : "");

/**
 * OGC-1020 (R1 of OGC-811) — the /Results worklist, the one results-entry
 * page: one toolbar (search, Lab Unit, date, status chips), a polymorphic
 * result cell (FR-A1), a per-row
 * read-only→Edit→Save edit-state machine (FR-A2/A3), e-signature on Save
 * (FR-A4), per-analysis save scoping + optimistic version check + soft
 * presence (FR-O1–O3), and cross-domain rendering driven by the selected Lab
 * Unit (FR-M1–M4).
 */

const breadcrumbs = [
  { label: "home.label", link: "/" },
  { label: "sidenav.label.results", link: "/Results" },
];

interface LabUnit {
  id: string;
  value: string;
  domain?: string;
}

interface WorklistRow extends ResultCellRow, PanelRow {
  accessionNumber?: string;
  sequenceNumber?: string;
  testName?: string;
  patientInfo?: string;
  patientName?: string;
  sampleType?: string;
  normalRange?: string;
  rangeNotAppliedReason?: string | null;
  analysisStatusId?: string;
  analysisLastupdated?: string;
  testResultComponentId?: string;
  testMethod?: string;
  analyzerId?: string;
  /** analysis-level non-conformity flag, computed server-side (QAService). */
  nonconforming?: boolean;
  [key: string]: unknown;
}

interface StatusOption {
  id: string;
  value: string;
}

/**
 * The worklist load's response. An error body arrives through the same
 * callback as a good one, so the shape has to admit both (OGC-1170).
 */
interface WorklistResponse {
  testResult?: WorklistRow[];
  paging?: { currentPage?: string | number; totalPages?: string | number };
  status?: number;
  error?: string;
}

interface SaveResponse {
  status?: number;
  error?: string;
  modifiedBy?: string;
  modifiedAt?: string;
  analysisLastupdated?: string;
  /** persisted result id — the saved row adopts it so re-saves UPDATE */
  resultId?: string;
  /** the analysis status the save moved the row to (Status column + counts) */
  analysisStatusId?: string;
  /** each part's status after the save, by component id (multi-part tests) */
  componentStatusIds?: Record<string, string>;
  /** the persisted value, as reported and as stored (OGC-1179) */
  resultValue?: string;
  rawResultValue?: string;
  reflex?: string[];
  calculated?: string[];
}

/**
 * Embedded in another screen (the Notebook's results modal, an
 * Immunohistochemistry case), the worklist shows one order's rows and
 * nothing else: no breadcrumb, heading or toolbar, and the address is left
 * alone. {@code includeFinished} also lists finalized results.
 */
export interface UnifiedResultsProps {
  accessionNumber?: string;
  embedded?: boolean;
  includeFinished?: boolean;
}

const UnifiedResults: React.FC<UnifiedResultsProps> = ({
  accessionNumber: embeddedAccession,
  embedded = false,
  includeFinished = false,
}) => {
  const intl = useIntl();
  const syncUrl = (urlState: URLSearchParams) => {
    if (!embedded) {
      replaceResultsUrl(urlState);
    }
  };
  const { addNotification, setNotificationVisible } =
    useContext(NotificationContext);

  const [labUnits, setLabUnits] = useState<LabUnit[]>([]);
  const [selectedLabUnit, setSelectedLabUnit] = useState<string>("");
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [collectionDate, setCollectionDate] = useState<string>("");
  const [selectedPatient, setSelectedPatient] = useState<PatientRecord | null>(
    null,
  );
  const [showPatientSearch, setShowPatientSearch] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [rows, setRows] = useState<WorklistRow[]>([]);
  const [rowStates, setRowStates] = useState<Record<string, RowEditState>>({});
  const [staleInfo, setStaleInfo] = useState<
    Record<string, { modifiedBy?: string; modifiedAt?: string }>
  >({});
  const [editingAnalysisId, setEditingAnalysisId] = useState<string | null>(
    null,
  );
  // The server's page announcement for the worklist shown, and the rows a full
  // server page holds; Carbon's items per page is pinned to the latter so
  // Carbon's page is the server's page.
  const [paging, setPaging] = useState<{
    currentPage?: string | number;
    totalPages?: string | number;
  }>();
  const [serverPageSize, setServerPageSize] = useState<number | undefined>();
  // The worklist request last sent, so a page of it can be asked for.
  const worklistUrl = useRef<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<boolean>(false);
  // ---- R2 (OGC-1021) panel state ----
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [methods, setMethods] = useState<IdValue[]>([]);
  const [analyzers, setAnalyzers] = useState<IdValue[]>([]);
  const [noteDrafts, setNoteDrafts] = useState<Record<string, NoteDraft>>({});
  const [dilutionDrafts, setDilutionDrafts] = useState<
    Record<string, DilutionDraft>
  >({});
  // analyzerId as loaded — the provenance snapshot for FR-B2
  const [loadedAnalyzers, setLoadedAnalyzers] = useState<
    Record<string, string>
  >({});
  const [sectionLayout, setSectionLayout] = useState<SectionLayout>(() =>
    loadSectionLayout(),
  );
  // ---- R4 (OGC-1023) NCE / referral / rejection state ----
  const { configurationProperties } = useContext(ConfigurationContext) as {
    configurationProperties?: Record<string, string>;
  };
  const dayFirst = configurationProperties?.DEFAULT_DATE_LOCALE === "fr-FR";
  const parseFilterDate = (text: string): Date | false =>
    parseIsoDate(displayDateToIso(text.trim(), dayFirst ? "fr-FR" : "en-US"));
  const commitFilterDate = (date: Date | false) => {
    if (!date) {
      setCollectionDate("");
      return;
    }
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    setCollectionDate(
      dayFirst
        ? `${dd}/${mm}/${date.getFullYear()}`
        : `${mm}/${dd}/${date.getFullYear()}`,
    );
  };
  const allowResultRejection =
    configurationProperties?.allowResultRejection === "true";
  const alertInvalidResults =
    configurationProperties?.ALERT_FOR_INVALID_RESULTS === "true";
  // OGC-1417: the value a row's modal is asking about, and per row the value
  // its user already confirmed or acknowledged
  const [resultAlert, setResultAlert] = useState<{
    key: string;
    row: WorklistRow;
    alerts: ResultAlert[];
    phase: "entry" | "beforeSign" | "refusal";
    customCriticalMessage?: string;
  } | null>(null);
  const confirmedRef = useRef<
    Record<string, { value: string; kinds: string[] }>
  >({});
  const decisionRef = useRef<{
    resolve: () => void;
    reject: (reason: Error) => void;
  } | null>(null);
  const [nceOpenKey, setNceOpenKey] = useState<string | null>(null);
  const [referralOrganizations, setReferralOrganizations] = useState<IdValue[]>(
    [],
  );
  const [referralReasons, setReferralReasons] = useState<IdValue[]>([]);
  const [rejectReasons, setRejectReasons] = useState<IdValue[]>([]);
  const [referralDrafts, setReferralDrafts] = useState<
    Record<string, ReferralDraft>
  >({});
  const [rejectDrafts, setRejectDrafts] = useState<Record<string, RejectDraft>>(
    {},
  );
  // The date the reference laboratory reported a result that is being typed in
  // here, per referred row.
  const [referenceLabReportDates, setReferenceLabReportDates] = useState<
    Record<string, string>
  >({});
  const [interpretationDrafts, setInterpretationDrafts] = useState<
    Record<string, string>
  >({});
  const [nceDisposition, setNceDisposition] = useState<NceDisposition>("NONE");
  const [nceRejectReasonId, setNceRejectReasonId] = useState<string>("");

  const domain: ResultsDomain = useMemo(() => {
    const unit = labUnits.find((u) => u.id === selectedLabUnit);
    return normalizeDomain(unit?.domain);
  }, [labUnits, selectedLabUnit]);

  useEffect(() => {
    getFromOpenElisServer("/rest/results-entry/lab-units", (list: LabUnit[]) =>
      setLabUnits(list || []),
    );
    getFromOpenElisServer(
      "/rest/analysis-status-types",
      (list: StatusOption[]) =>
        setStatusOptions((list || []).filter((s) => s.id !== "0")),
    );
    // R2 (FR-B1): Method and Analyzer are separate fields with their own lists
    getFromOpenElisServer("/rest/displayList/METHODS", (list: IdValue[]) =>
      setMethods(list || []),
    );
    getFromOpenElisServer(
      "/rest/displayList/ANALYZER_LIST",
      (list: IdValue[]) => setAnalyzers(list || []),
    );
    // R4 (FR-F2/E3): referral target + reason lists, rejection reasons
    getFromOpenElisServer(
      "/rest/displayList/REFERRAL_ORGANIZATIONS",
      (list: IdValue[]) => setReferralOrganizations(list || []),
    );
    getFromOpenElisServer(
      "/rest/displayList/REFERRAL_REASONS",
      (list: IdValue[]) => setReferralReasons(list || []),
    );
    getFromOpenElisServer(
      "/rest/displayList/REJECTION_REASONS",
      (list: IdValue[]) => setRejectReasons(list || []),
    );
  }, []);

  const rowsRef = useRef<WorklistRow[]>(rows);
  rowsRef.current = rows;
  const rowStatesRef = useRef(rowStates);
  rowStatesRef.current = rowStates;

  const applyLoadedRows = useCallback(
    (results: WorklistResponse | undefined, savedRowKey?: string) => {
      const keepUnsaved = savedRowKey !== undefined;
      // A worklist that failed to load and a worklist with nothing in it look
      // the same once the rows are empty, and the page said nothing either
      // way: a technician was shown an empty queue with no sign the request
      // had failed, while the work sat there undone (OGC-1170).
      //
      // getFromOpenElisServer hands the callback whatever JSON came back,
      // error bodies included, so the response is checked the same way the
      // save path on this page checks its own.
      if (!results || (results.status && results.status >= 400)) {
        setLoadError(true);
        setRows([]);
        setRowStates({});
        setLoading(false);
        return;
      }
      setLoadError(false);
      let loaded = (results?.testResult || []).filter((r) => r.analysisId);
      const unsaved: Record<string, WorklistRow> = {};
      if (keepUnsaved) {
        for (const row of rowsRef.current) {
          const key = worklistRowKey(row);
          if (
            key !== savedRowKey &&
            showSave(rowStatesRef.current[key] || "EMPTY")
          ) {
            unsaved[key] = row;
          }
        }
        loaded = loaded.map((row) => {
          const edited = unsaved[worklistRowKey(row)];
          return edited
            ? {
                ...row,
                resultValue: edited.resultValue,
                multiSelectResultValues: edited.multiSelectResultValues,
                testMethod: edited.testMethod,
                analyzerId: edited.analyzerId,
              }
            : row;
        });
      }
      setRows(loaded);
      setPaging(results?.paging);
      setServerPageSize((previous) =>
        serverPageSizeOf(results?.paging, loaded.length, previous),
      );
      const states: Record<string, RowEditState> = {};
      for (const row of loaded) {
        // one analysis may render N component rows (FR-A′1) — each row keeps
        // its own edit state under its composite key
        states[worklistRowKey(row)] = initialRowState(
          Boolean(row.resultValue) ||
            Boolean(
              row.multiSelectResultValues &&
              row.multiSelectResultValues !== "{}",
            ),
        );
        if (unsaved[worklistRowKey(row)]) {
          states[worklistRowKey(row)] =
            rowStatesRef.current[worklistRowKey(row)];
        }
      }
      setRowStates(states);
      const loadedByKey: Record<string, string> = {};
      for (const row of loaded) {
        if (row.analyzerId) {
          loadedByKey[worklistRowKey(row)] = row.analyzerId;
        }
      }
      setLoadedAnalyzers(loadedByKey);
      if (!keepUnsaved) {
        setNoteDrafts({});
        setDilutionDrafts({});
        setReferralDrafts({});
        setRejectDrafts({});
        setInterpretationDrafts({});
        setNceOpenKey(null);
        setExpandedRowKey(null);
        setStaleInfo({});
        setEditingAnalysisId(null);
      }
      setLoading(false);
    },
    [],
  );

  /** One server page of the worklist last requested, asked for by Carbon's pagination. */
  const loadWorklistPage = useCallback(
    (pageNumber: number) => {
      if (!worklistUrl.current) {
        return;
      }
      setLoading(true);
      getFromOpenElisServer(
        worklistUrl.current + "&page=" + pageNumber,
        applyLoadedRows,
      );
    },
    [applyLoadedRows],
  );

  /**
   * Loads the worklist for the current filters, or for one patient. A patient
   * search sends only {@code patientPK}: /rest/LogbookResults treats a lab
   * unit or date sent alongside as the primary criterion and never reaches
   * its patient branch. {@code patientOverride} {@code null} forces the filter
   * mode while the state still holds a patient (the Clear button).
   */
  const loadWorklist = useCallback(
    (
      labNumberOverride?: string,
      patientOverride?: PatientRecord | null,
      savedRowKey?: string,
    ) => {
      setLoading(true);
      const params = new URLSearchParams();
      // guard: when wired directly to onClick the argument is the click
      // event — only a string counts as an override
      const labNumber =
        typeof labNumberOverride === "string" ? labNumberOverride : searchText;
      const patient =
        patientOverride === undefined ? selectedPatient : patientOverride;
      const patientPK = patient?.patientPK || "";
      if (patientPK) {
        params.set("patientPK", patientPK);
      } else {
        if (labNumber) {
          params.set("labNumber", labNumber);
        }
        if (selectedLabUnit) {
          params.set("testSectionId", selectedLabUnit);
        }
        if (collectionDate) {
          params.set("collectionDate", collectionDate);
        }
      }
      params.set("doRange", "false");
      params.set("finished", includeFinished ? "true" : "false");
      worklistUrl.current = "/rest/LogbookResults?" + params.toString();
      getFromOpenElisServer(worklistUrl.current, (results?: WorklistResponse) =>
        applyLoadedRows(results, savedRowKey),
      );
      // FRS: the selected Lab Unit (and filters) are the page's primary
      // state — keep them in the URL so refresh and share links reproduce
      // the same worklist
      const urlState = new URLSearchParams(window.location.search);
      const setOrDrop = (key: string, value: string) =>
        value ? urlState.set(key, value) : urlState.delete(key);
      setOrDrop("patientId", patientPK);
      setOrDrop("accessionNumber", patientPK ? "" : labNumber);
      setOrDrop("testSectionId", patientPK ? "" : selectedLabUnit);
      setOrDrop("collectionDate", patientPK ? "" : collectionDate);
      syncUrl(urlState);
    },
    [
      searchText,
      selectedLabUnit,
      collectionDate,
      selectedPatient,
      applyLoadedRows,
      embedded,
      includeFinished,
    ],
  );

  /** The patient form auto-selects any ?patientId= it finds on mount, so the parameter goes before the form opens. */
  const openPatientSearch = () => {
    const urlState = new URLSearchParams(window.location.search);
    urlState.delete("patientId");
    syncUrl(urlState);
    setShowPatientSearch(true);
  };

  const selectPatient = (patient: PatientRecord) => {
    setSelectedPatient(patient);
    setShowPatientSearch(false);
    loadWorklist(undefined, patient);
  };

  const clearPatient = () => {
    setSelectedPatient(null);
    setShowPatientSearch(false);
    if (selectedLabUnit || searchText) {
      loadWorklist(undefined, null);
      return;
    }
    setRows([]);
    setRowStates({});
    const urlState = new URLSearchParams(window.location.search);
    urlState.delete("patientId");
    syncUrl(urlState);
  };

  useEffect(() => {
    if (selectedLabUnit) {
      loadWorklist();
    }
  }, [selectedLabUnit]);

  // Restore the worklist from the URL: deep links from the in-progress
  // dashboard (?accessionNumber=) and refreshes of a loaded page
  // (?testSectionId=&collectionDate=&status=) reproduce the same view
  useEffect(() => {
    if (embedded) {
      if (embeddedAccession) {
        setSearchText(embeddedAccession);
        loadWorklist(embeddedAccession);
      }
      return;
    }
    const urlState = new URLSearchParams(window.location.search);
    const accession = urlState.get("accessionNumber");
    const unit = urlState.get("testSectionId");
    const date = urlState.get("collectionDate");
    const status = urlState.get("status");
    const patientId = urlState.get("patientId");
    if (status) {
      setStatusFilter(status);
    }
    if (patientId) {
      getFromOpenElisServer(
        "/rest/patient-details?patientID=" + encodeURIComponent(patientId),
        (details: PatientRecord | undefined) => {
          if (details?.patientPK) {
            setSelectedPatient(details);
            loadWorklist(undefined, details);
          }
        },
      );
      return;
    }
    if (date) {
      setCollectionDate(date);
    }
    if (accession) {
      setSearchText(accession);
    }
    if (unit) {
      setSelectedLabUnit(unit); // triggers the worklist load effect
    } else if (accession) {
      loadWorklist(accession);
    }
  }, []);

  // Keep the status chip in the URL too (client-side filter, no refetch)
  useEffect(() => {
    if (embedded) {
      return;
    }
    const urlState = new URLSearchParams(window.location.search);
    if (statusFilter === "ALL") {
      urlState.delete("status");
    } else {
      urlState.set("status", statusFilter);
    }
    const query = urlState.toString();
    window.history.replaceState(
      null,
      "",
      query ? `/Results?${query}` : "/Results",
    );
  }, [statusFilter]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of rows) {
      const key = row.analysisStatusId || "";
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [rows]);

  const filteredRows = useMemo(
    () =>
      statusFilter === "ALL"
        ? rows
        : rows.filter((row) => row.analysisStatusId === statusFilter),
    [rows, statusFilter],
  );

  const pagedRows = filteredRows;
  const arrows = serverPageArrowsProps({
    paging,
    onPageRequest: loadWorklistPage,
  });

  const visibleAnalysisIds = useMemo(
    () => pagedRows.map((row) => row.analysisId),
    [pagedRows],
  );

  const presence = usePresence(editingAnalysisId, visibleAnalysisIds);

  const handleValueChange = useCallback(
    (
      target: WorklistRow,
      field: "resultValue" | "multiSelectResultValues",
      value: string,
    ) => {
      // FR-A′3: a multi-component analysis renders one row per component —
      // update ONLY the edited row (keyed by analysisId + componentId), never
      // its sibling component rows
      const key = worklistRowKey(target);
      setRows((current) =>
        current.map((row) =>
          worklistRowKey(row) === key ? { ...row, [field]: value } : row,
        ),
      );
      setRowStates((current) => ({
        ...current,
        [key]: nextRowState(current[key] || "EMPTY", {
          type: "VALUE_CHANGED",
        }),
      }));
      // FR-O3: entering a fresh result counts as having the analysis "open
      // in Edit" — colleagues should see the presence hint for this row too
      setEditingAnalysisId(target.analysisId);
    },
    [],
  );

  // R2 (FR-B1): method/analyzer edits are part of the analysis record — they
  // participate in the same edit-state machine as the value itself
  const handleFieldChange = useCallback(
    (
      target: WorklistRow,
      field: "testMethod" | "analyzerId",
      value: string,
    ) => {
      const key = worklistRowKey(target);
      setRows((current) =>
        current.map((row) =>
          worklistRowKey(row) === key ? { ...row, [field]: value } : row,
        ),
      );
      setRowStates((current) => ({
        ...current,
        [key]: nextRowState(current[key] || "EMPTY", {
          type: "VALUE_CHANGED",
        }),
      }));
      setEditingAnalysisId(target.analysisId);
    },
    [],
  );

  // R4: a referral or rejection is part of the analysis record — setting a
  // draft dirties the row so it goes through the same Save (e-signature)
  const markRowDirty = useCallback((target: WorklistRow) => {
    const key = worklistRowKey(target);
    setRowStates((current) => ({
      ...current,
      [key]: nextRowState(current[key] || "EMPTY", { type: "VALUE_CHANGED" }),
    }));
    setEditingAnalysisId(target.analysisId);
  }, []);

  const handleReferralDraftChange = useCallback(
    (target: WorklistRow, draft: ReferralDraft | null) => {
      const key = worklistRowKey(target);
      setReferralDrafts((current) => {
        const next = { ...current };
        if (draft) {
          next[key] = draft;
        } else {
          delete next[key];
        }
        return next;
      });
      // Referring a test out is not a change to its result, so it makes an
      // already saved row savable without unlocking the value or recording the
      // save as a revision. Without this a confirmation referral, which is raised
      // precisely when a result already exists, could not be saved at all.
      // Withdrawing the referral again takes the row back to plain saved.
      setRowStates((current) => ({
        ...current,
        [key]: nextRowState(current[key] || "EMPTY", {
          type: draft ? "DISPOSITION_CHANGED" : "DISPOSITION_CLEARED",
        }),
      }));
      if (draft) {
        setEditingAnalysisId(target.analysisId);
      }
    },
    [],
  );

  /**
   * The reference laboratory's own report date belongs to the referral, so
   * recording it makes an already-saved row savable without unlocking the
   * result or counting the save as a revision of it.
   */
  const handleReferenceLabReportDateChange = useCallback(
    (target: WorklistRow, value: string) => {
      const key = worklistRowKey(target);
      setReferenceLabReportDates((current) => ({ ...current, [key]: value }));
      setRowStates((current) => ({
        ...current,
        [key]: nextRowState(current[key] || "EMPTY", {
          type: value.trim() ? "DISPOSITION_CHANGED" : "DISPOSITION_CLEARED",
        }),
      }));
    },
    [],
  );

  const handleRejectDraftChange = useCallback(
    (target: WorklistRow, draft: RejectDraft | null) => {
      const key = worklistRowKey(target);
      setRejectDrafts((current) => {
        const next = { ...current };
        if (draft) {
          next[key] = draft;
        } else {
          delete next[key];
        }
        return next;
      });
      if (draft) {
        markRowDirty(target);
      }
    },
    [markRowDirty],
  );

  const handleInterpretationDraftChange = useCallback(
    (target: WorklistRow, draft: string | null) => {
      const key = worklistRowKey(target);
      setInterpretationDrafts((current) => {
        const next = { ...current };
        if (draft !== null) {
          next[key] = draft;
        } else {
          delete next[key];
        }
        return next;
      });
      if (draft !== null) {
        markRowDirty(target);
      }
    },
    [markRowDirty],
  );

  // FR-E3: the disposition chosen alongside an NCE applies once the NCE is
  // filed. Cancel/Retest call their shipped endpoints; Reject arms the legacy
  // rejection on the row so the e-signature Save applies it (FR-A4).
  const handleNceApplyDisposition = useCallback(
    (target: WorklistRow, disposition: NceDisposition, reasonId: string) => {
      // the NCE itself is filed by this point (this runs on submit success);
      // surface the analysis-level flag immediately — a full reload recomputes
      // it server-side (QAService)
      setRows((current) =>
        current.map((row) =>
          row.analysisId === target.analysisId
            ? { ...row, nonconforming: true }
            : row,
        ),
      );
      if (disposition === "REJECT") {
        if (reasonId) {
          handleRejectDraftChange(target, { rejectReasonId: reasonId });
          addNotification({
            kind: NotificationKinds.info,
            title: intl.formatMessage({ id: "label.results.nce.report" }),
            message: intl.formatMessage({
              id: "label.results.nce.disposition.rejectArmed",
            }),
          });
          setNotificationVisible(true);
        }
        return;
      }
      const requests = dispositionRequests(disposition, target);
      if (requests.length === 0) {
        return;
      }
      const runNext = (index: number) => {
        if (index >= requests.length) {
          addNotification({
            kind: NotificationKinds.success,
            title: intl.formatMessage({ id: "label.results.nce.report" }),
            message: intl.formatMessage({
              id:
                disposition === "CANCEL"
                  ? "label.results.nce.disposition.cancelled"
                  : "label.results.nce.disposition.retested",
            }),
          });
          setNotificationVisible(true);
          loadWorklist();
          return;
        }
        postToOpenElisServerJsonResponse(
          requests[index].url,
          JSON.stringify(requests[index].body),
          () => runNext(index + 1),
        );
      };
      runNext(0);
    },
    [
      handleRejectDraftChange,
      addNotification,
      setNotificationVisible,
      intl,
      loadWorklist,
    ],
  );

  const handleEdit = useCallback((target: WorklistRow) => {
    const key = worklistRowKey(target);
    // Edit the value that is stored, not the one that is reported. They differ
    // whenever reporting formats — a numeric result on a test configured for no
    // decimal places reports 23.7 as "23" — and an editor seeded from the
    // reported value writes it back over the stored one, truncating the
    // patient's result and booking the loss to the technician (OGC-1179).
    setRows((current) =>
      current.map((row) =>
        worklistRowKey(row) === key && typeof row.rawResultValue === "string"
          ? { ...row, resultValue: row.rawResultValue }
          : row,
      ),
    );
    setRowStates((current) => ({
      ...current,
      [key]: nextRowState(current[key] || "SAVED", {
        type: "EDIT_CLICKED",
      }),
    }));
    setEditingAnalysisId(target.analysisId);
  }, []);

  const handleSaveResponse = useCallback(
    (target: WorklistRow, response: SaveResponse | undefined) => {
      const key = worklistRowKey(target);
      if (response && response.status === 409) {
        // FR-O2: the stale editor loses — nothing merged, refresh offered.
        setStaleInfo((current) => ({
          ...current,
          [key]: {
            modifiedBy: response.modifiedBy,
            modifiedAt: response.modifiedAt,
          },
        }));
        setRowStates((current) => ({
          ...current,
          [key]: nextRowState(current[key] || "EDITING", {
            type: "SAVE_REJECTED_STALE",
          }),
        }));
        return;
      }
      if (!response || requestFailed(response)) {
        addNotification({
          title: intl.formatMessage({ id: "notification.title" }),
          message:
            serverMessage(response) ||
            intl.formatMessage({
              id:
                !response || response.status === 0
                  ? "error.results.save.noResponse"
                  : "error.save.msg",
            }),
          kind: NotificationKinds.error,
        });
        setNotificationVisible(true);
        return;
      }
      setRowStates((current) => ({
        ...current,
        [key]: nextRowState(current[key] || "EDITING", {
          type: "SAVE_SUCCEEDED",
        }),
      }));
      // the version token is per ANALYSIS — refresh it on every component row
      // of this analysis so a sibling save isn't falsely rejected. The status
      // follows the save too, so the Status column and the filter counts
      // describe the worklist as the save left it (OGC-1179); a component row
      // carries its own part's status, which the save reports per part.
      // The SAVED row must also adopt the persisted resultId: a row saved
      // from the blank placeholder state would otherwise keep resultId null
      // and every later save would INSERT a duplicate result for the
      // component instead of updating it. It adopts the persisted value in
      // both forms too, so a row edited twice without a reload edits what is
      // stored the second time as well.
      setRows((current) =>
        current.map((row) => {
          if (row.analysisId !== target.analysisId) {
            return row;
          }
          const updated = { ...row };
          if (response.analysisLastupdated) {
            updated.analysisLastupdated = response.analysisLastupdated;
          }
          const componentStatusId =
            row.testResultComponentId &&
            response.componentStatusIds?.[row.testResultComponentId];
          if (componentStatusId) {
            updated.analysisStatusId = componentStatusId;
          } else if (
            response.analysisStatusId &&
            (!row.testResultComponentId || worklistRowKey(row) === key)
          ) {
            updated.analysisStatusId = response.analysisStatusId;
          }
          if (worklistRowKey(row) === key) {
            if (response.resultId && !updated.resultId) {
              updated.resultId = response.resultId;
            }
            if (typeof response.rawResultValue === "string") {
              updated.rawResultValue = response.rawResultValue;
            }
            if (typeof response.resultValue === "string") {
              updated.resultValue = response.resultValue;
            }
          }
          return updated;
        }),
      );
      setStaleInfo((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      setEditingAnalysisId((current) =>
        current === target.analysisId ? null : current,
      );
      const triggered = [
        ...(response.reflex || []),
        ...(response.calculated || []),
      ];
      addNotification({
        title: intl.formatMessage({ id: "notification.title" }),
        message:
          intl.formatMessage({ id: "success.save.msg" }) +
          (triggered.length
            ? " " +
              intl.formatMessage({ id: "label.results.reflexTriggered" }) +
              " " +
              triggered.join(", ")
            : ""),
        kind: NotificationKinds.success,
      });
      setNotificationVisible(true);
      // A reflex or calculation adds analyses to this order that the save
      // response only names. Naming them in a toast and leaving the worklist
      // as it was asks the user to refresh to see the work they just caused,
      // so the rows are re-read when — and only when — some were generated,
      // keeping every other row's unsaved edit rather than discarding it.
      if (triggered.length) {
        loadWorklist(undefined, undefined, key);
      }
    },
    [addNotification, intl, setNotificationVisible, loadWorklist],
  );

  const saveRow = useCallback(
    (
      row: WorklistRow,
      acknowledged: { critical: boolean; invalid: boolean } = {
        critical: false,
        invalid: false,
      },
    ) => {
      // FR-O1: the payload names and carries exactly this analysis — never
      // the page. Untouched rows cannot be re-submitted or defaulted.
      const item: Record<string, unknown> = { ...row, isModified: true };
      item.criticalAcknowledged = acknowledged.critical;
      item.invalidResultConfirmed = acknowledged.invalid;
      // A referral saved against an already-saved result must leave that result
      // exactly as stored. The row carries the value the test reports, which is
      // rounded, so posting it back would quietly rewrite the stored one.
      if (!writesResultValue(rowStates[worklistRowKey(row)] || "EMPTY")) {
        item.resultValue = row.rawResultValue ?? row.resultValue;
      } else {
        item.rawResultValue = row.resultValue;
      }
      delete item.result;
      delete item.analysisNotes;
      // attachments live in order_attachment now (OGC-811); round-tripping
      // the legacy inline resultFile would clone a result_file row per save
      delete item.resultFile;
      // TestResultItem serializes reportable as "Y"/"N" but deserializes it
      // as boolean — same normalization the legacy page applies before POST
      item.reportable = item.reportable !== "N";
      const key = worklistRowKey(row);
      // A referral the user opened rides the save only when complete; one
      // missing its laboratory or reason is refused rather than dropped, so a
      // save never reads as having referred a test it did not.
      const pendingReferral = referralDrafts[key];
      if (
        pendingReferral &&
        (!pendingReferral.referredInstituteId ||
          !pendingReferral.referralReasonId)
      ) {
        addNotification({
          title: intl.formatMessage({ id: "notification.title" }),
          message: intl.formatMessage({
            id: "label.results.referral.incomplete",
          }),
          kind: NotificationKinds.error,
        });
        setNotificationVisible(true);
        return;
      }
      // R2 (FR-J1/J2): the dual-axis note — visibility chosen, context auto-set
      // from the edit-state machine (Modification when editing a saved result)
      const noteDraft = noteDrafts[key];
      if (noteDraft && noteDraft.text.trim()) {
        item.note = noteDraft.text.trim();
        item.noteVisibility = noteDraft.visibility;
        item.noteContext = isModifyingSavedResult(rowStates[key] || "EMPTY")
          ? "MODIFICATION"
          : "ENTRY";
      }
      // R2 (FR-D5): dilution provenance — the reported value is already in
      // resultValue; factor + measured value ride along for the audit note
      const dilution = dilutionDrafts[key];
      if (dilution && dilution.factor.trim()) {
        item.dilutionFactor = dilution.factor.trim();
        item.measuredValue = dilution.measuredValue.trim();
      }
      // R4 (FR-F2/F3): the referral rides the row's save — legacy
      // handleReferrals path; the referred test is this row's own test (no
      // test-to-perform field per FRS)
      const referral = referralDrafts[key];
      if (
        referral &&
        referral.referredInstituteId &&
        referral.referralReasonId
      ) {
        item.refer = true;
        item.referredOut = true;
        item.referralItem = {
          referralReasonId: referral.referralReasonId,
          referredInstituteId: referral.referredInstituteId,
          referredSendDate: referral.referredSendDate,
          referredTestId: row.testId,
        };
      }
      // A result typed in for a test already at a reference laboratory: carry
      // that laboratory's own report date so the referral records it.
      const reportedOn = referenceLabReportDates[key];
      if (row.referredOut && reportedOn && reportedOn.trim()) {
        item.referralItem = {
          ...(item.referralItem || {}),
          referredReportDate: reportedOn.trim(),
        };
      }
      // R4 (FR-E3): reject disposition — legacy shadowRejected mechanics
      // (clears the value, writes the rejection-reason note, TechnicalRejected)
      const reject = rejectDrafts[key];
      if (reject && reject.rejectReasonId) {
        item.rejected = true;
        item.shadowRejected = true;
        item.rejectReasonId = reject.rejectReasonId;
      }
      // R7 (FR-G1): interpretation rides the save as a report-visible note
      const interpretation = interpretationDrafts[key];
      if (interpretation && interpretation.trim()) {
        item.interpretation = interpretation.trim();
      }
      postToOpenElisServerJsonResponse(
        `/rest/results-entry/analysis/${row.analysisId}/result`,
        JSON.stringify({ testResult: item }),
        (response: SaveResponse | undefined) => {
          const refusal = acknowledgementRefusal(response);
          if (refusal) {
            setResultAlert({
              key,
              row,
              alerts: refusal.alerts,
              phase: "refusal",
              customCriticalMessage: refusal.customCriticalMessage,
            });
            return;
          }
          handleSaveResponse(row, response);
          if (!requestFailed(response)) {
            setNoteDrafts((current) => {
              const next = { ...current };
              delete next[key];
              return next;
            });
            setDilutionDrafts((current) => {
              const next = { ...current };
              delete next[key];
              return next;
            });
            setReferralDrafts((current) => {
              const next = { ...current };
              delete next[key];
              return next;
            });
            // The referral now holds the date, so the row must not carry it
            // into its next save the way a draft would.
            setReferenceLabReportDates((current) => {
              const next = { ...current };
              delete next[key];
              return next;
            });
            setRejectDrafts((current) => {
              const next = { ...current };
              delete next[key];
              return next;
            });
            setInterpretationDrafts((current) => {
              const next = { ...current };
              delete next[key];
              return next;
            });
          }
        },
      );
    },
    [
      handleSaveResponse,
      noteDrafts,
      dilutionDrafts,
      referralDrafts,
      referenceLabReportDates,
      rejectDrafts,
      interpretationDrafts,
      rowStates,
      addNotification,
      setNotificationVisible,
      intl,
    ],
  );

  const owedAlerts = useCallback(
    (row: WorklistRow) =>
      owedResultAlerts(row, {
        alertInvalidResults,
        writesValue: writesResultValue(
          rowStates[worklistRowKey(row)] || "EMPTY",
        ),
      }),
    [alertInvalidResults, rowStates],
  );

  const isConfirmed = (key: string, alert: ResultAlert) => {
    const confirmed = confirmedRef.current[key];
    return Boolean(
      confirmed &&
      confirmed.value === alert.value &&
      confirmed.kinds.includes(alert.kind),
    );
  };

  const markConfirmed = (key: string, alerts: ResultAlert[]) => {
    if (!alerts.length) {
      return;
    }
    const previous = confirmedRef.current[key];
    const kinds =
      previous && previous.value === alerts[0].value ? previous.kinds : [];
    confirmedRef.current = {
      ...confirmedRef.current,
      [key]: {
        value: alerts[0].value,
        kinds: [...kinds, ...alerts.map((alert) => alert.kind)],
      },
    };
  };

  // OGC-1417: a value outside the valid range is questioned as soon as the
  // field is left, keeping what was typed so it can be corrected. Leaving it
  // for a button (Save) is not leaving it: Save asks before signing.
  const handleValueBlur = useCallback(
    (row: WorklistRow, nextFocus?: EventTarget | null) => {
      if (nextFocus instanceof HTMLElement && nextFocus.closest("button")) {
        return;
      }
      const key = worklistRowKey(row);
      const alerts = owedAlerts(row).filter(
        (alert) => alert.kind === "INVALID" && !isConfirmed(key, alert),
      );
      if (alerts.length) {
        setResultAlert({ key, row, alerts, phase: "entry" });
      }
    },
    [owedAlerts],
  );

  // OGC-1417: a critical value is acknowledged, and a value outside the valid
  // range confirmed, before the save is signed: the pop-up comes first and the
  // signature only once the user has answered it.
  const beforeSign = useCallback(
    (row: WorklistRow) =>
      new Promise<void>((resolve, reject) => {
        const key = worklistRowKey(row);
        const alerts = owedAlerts(row).filter(
          (alert) => !isConfirmed(key, alert),
        );
        if (!alerts.length) {
          resolve();
          return;
        }
        decisionRef.current = { resolve, reject };
        setResultAlert({
          key,
          row,
          alerts,
          phase: "beforeSign",
          customCriticalMessage: configurationProperties?.customCriticalMessage,
        });
      }),
    [owedAlerts, configurationProperties],
  );

  const handleSave = useCallback(
    (row: WorklistRow) => {
      const key = worklistRowKey(row);
      const confirmed = owedAlerts(row).filter((alert) =>
        isConfirmed(key, alert),
      );
      saveRow(row, {
        critical: confirmed.some((alert) => alert.kind === "CRITICAL"),
        invalid: confirmed.some((alert) => alert.kind === "INVALID"),
      });
    },
    [owedAlerts, saveRow],
  );

  const confirmResultAlert = () => {
    if (!resultAlert) {
      return;
    }
    const { key, alerts, phase } = resultAlert;
    setResultAlert(null);
    markConfirmed(key, alerts);
    if (phase === "beforeSign") {
      decisionRef.current?.resolve();
      decisionRef.current = null;
    } else if (phase === "refusal") {
      const latest =
        rows.find((candidate) => worklistRowKey(candidate) === key) ||
        resultAlert.row;
      saveRow(latest, {
        critical: alerts.some((alert) => alert.kind === "CRITICAL"),
        invalid: alerts.some((alert) => alert.kind === "INVALID"),
      });
    }
  };

  const correctResultAlert = () => {
    if (!resultAlert) {
      return;
    }
    const inputId = `unifiedResultValue-${resultAlert.key}`;
    setResultAlert(null);
    decisionRef.current?.reject(new Error("corrected"));
    decisionRef.current = null;
    window.setTimeout(() => document.getElementById(inputId)?.focus(), 0);
  };

  // Gallery parity: accession leads (mono accent), identity as a sub-line, a
  // patient initials avatar on clinical rows (FR-M2/M3: no patient identity
  // outside CLINICAL — sample context replaces it)
  const subjectCell = (row: WorklistRow): React.ReactNode => {
    const accession = row.accessionNumber || "";
    const subline =
      domain === "CLINICAL"
        ? [row.patientName, row.patientInfo].filter(Boolean).join(" · ")
        : row.sampleType || "";
    return (
      <div className="unifiedSubjectCell">
        {domain === "CLINICAL" && (
          <Avatar name={row.patientName} id={row.patientId as string} />
        )}
        <div>
          <div className="unifiedAccession">{accession}</div>
          {subline && <div className="unifiedSubjectSub">{subline}</div>}
          {/* the old Results page's non-conformity indicator, reproduced
              under the sample/patient identity — analysis-level, shared by
              every component row */}
          {row.nonconforming && (
            <picture>
              <img
                src={config.serverBaseUrl + "/images/nonconforming.gif"}
                alt="nonconforming"
                width="20"
                height="15"
                data-testid={`nonconforming-${worklistRowKey(row)}`}
              />
            </picture>
          )}
        </div>
      </div>
    );
  };

  const statusName = (statusId?: string): string =>
    statusOptions.find((s) => s.id === statusId)?.value || statusId || "";

  /** Carbon tag color per status name — never conveys status by color alone. */
  const statusTagType = (statusId?: string): string => {
    const name = statusName(statusId).toLowerCase();
    if (name.includes("final")) return "green";
    if (name.includes("reject") || name.includes("cancel")) return "red";
    if (name.includes("acceptance")) return "teal";
    if (name.includes("not tested") || name.includes("pending")) return "gray";
    return "cool-gray";
  };

  return (
    <>
      <AlertDialog />
      {!embedded && <PageBreadCrumb breadcrumbs={breadcrumbs} />}
      {loading && (
        <Loading
          description={intl.formatMessage({ id: "label.results.loading" })}
          withOverlay={!embedded}
        />
      )}
      <Grid
        fullWidth
        className={`unifiedResultsPage${embedded ? " unifiedResultsEmbedded" : ""}`}
        data-testid={embedded ? "unified-results-embedded" : undefined}
      >
        {!embedded && (
          <Column lg={16} md={8} sm={4}>
            <Section>
              <Heading>
                <FormattedMessage id="sidenav.label.results" />
                {domain !== "CLINICAL" && (
                  <Tag type="cyan" className="unifiedResultsDomainTag">
                    {formatDomainMessage(intl, "label.results.domain", domain)}
                  </Tag>
                )}
              </Heading>
            </Section>
          </Column>
        )}

        {/* Toolbar: search + Lab Unit + date + patient (FR worklist toolbar) */}
        {!embedded && (
          <>
            <Column
              lg={3}
              md={4}
              sm={4}
              className="unifiedResultsToolbarColumn"
            >
              {/* Carbon Search's labelText is visually hidden; render an explicit
              label so the toolbar fields align on one horizontal level */}
              <div className="cds--label">
                <FormattedMessage id="label.button.search" />
              </div>
              <Search
                id="unifiedResultsSearch"
                labelText={intl.formatMessage({ id: "label.results.search" })}
                placeholder={intl.formatMessage({ id: "label.results.search" })}
                value={searchText}
                disabled={Boolean(selectedPatient)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchText(e.target.value)
                }
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter") {
                    loadWorklist();
                  }
                }}
              />
            </Column>
            <Column
              lg={3}
              md={4}
              sm={4}
              className="unifiedResultsToolbarColumn"
            >
              <Select
                id="unifiedResultsLabUnit"
                labelText={intl.formatMessage({ id: "label.results.labUnit" })}
                value={selectedLabUnit}
                disabled={Boolean(selectedPatient)}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setSelectedLabUnit(e.target.value)
                }
              >
                <SelectItem text="" value="" />
                {labUnits.map((unit) => (
                  <SelectItem text={unit.value} value={unit.id} key={unit.id} />
                ))}
              </Select>
            </Column>
            <Column
              lg={3}
              md={4}
              sm={4}
              className="unifiedResultsToolbarColumn"
            >
              <DatePicker
                key={dayFirst ? "day-first" : "month-first"}
                datePickerType="single"
                dateFormat={dayFirst ? "d/m/Y" : "m/d/Y"}
                parseDate={parseFilterDate}
                onChange={(dates: Date[]) =>
                  commitFilterDate(dates && dates.length ? dates[0] : false)
                }
              >
                <DatePickerInput
                  id="unifiedResultsDate"
                  labelText={intl.formatMessage({ id: "label.results.date" })}
                  placeholder={intl.formatMessage({
                    id: datePlaceholderId(
                      configurationProperties?.DEFAULT_DATE_LOCALE,
                    ),
                  })}
                  disabled={Boolean(selectedPatient)}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    const typed = event.target.value.trim();
                    commitFilterDate(typed ? parseFilterDate(typed) : false);
                  }}
                />
              </DatePicker>
            </Column>
            <Column
              lg={3}
              md={2}
              sm={4}
              className="unifiedResultsToolbarColumn unifiedResultsPatientColumn"
            >
              <div className="cds--label">&nbsp;</div>
              <Button
                kind="tertiary"
                size="md"
                data-testid="search-by-patient"
                onClick={() =>
                  showPatientSearch
                    ? setShowPatientSearch(false)
                    : openPatientSearch()
                }
                disabled={loading}
              >
                <FormattedMessage id="label.results.searchByPatient" />
              </Button>
            </Column>
            <Column
              lg={4}
              md={2}
              sm={4}
              className="unifiedResultsToolbarColumn unifiedResultsLoadColumn"
            >
              {/* spacer keeps the button on the same level as the labeled fields */}
              <div className="cds--label">&nbsp;</div>
              <Button
                size="md"
                onClick={() => loadWorklist()}
                disabled={loading}
              >
                <FormattedMessage id="label.results.load" />
              </Button>
            </Column>
          </>
        )}

        {!embedded && (showPatientSearch || selectedPatient) && (
          <Column lg={16} md={8} sm={4}>
            <div
              className="bordered-section-panel unifiedResultsPatientPanel"
              data-testid="patient-search-panel"
            >
              <div className="unifiedResultsPatientPanelHeader">
                <Tag
                  type={selectedPatient?.patientPK ? "blue" : "gray"}
                  data-testid="selected-patient"
                >
                  <FormattedMessage id="label.results.selectedPatient" />:{" "}
                  {selectedPatient?.patientPK
                    ? patientDisplayName(selectedPatient)
                    : intl.formatMessage({
                        id: "label.results.selectedPatient.none",
                      })}
                </Tag>
                {selectedPatient?.patientPK && (
                  <>
                    <Button
                      kind="ghost"
                      size="sm"
                      data-testid="select-another-patient"
                      onClick={() =>
                        showPatientSearch
                          ? setShowPatientSearch(false)
                          : openPatientSearch()
                      }
                    >
                      <FormattedMessage id="label.results.selectAnotherPatient" />
                    </Button>
                    <Button
                      kind="ghost"
                      size="sm"
                      data-testid="clear-patient"
                      onClick={clearPatient}
                    >
                      <FormattedMessage id="label.button.clear" />
                    </Button>
                  </>
                )}
              </div>
              {showPatientSearch && (
                <div className="unifiedResultsPatientSearch">
                  <SearchPatientForm getSelectedPatient={selectPatient} />
                </div>
              )}
            </div>
          </Column>
        )}

        {/* Status filter chips with counts */}
        <Column lg={16} md={8} sm={4} className="unifiedResultsChips">
          <Tag
            type={statusFilter === "ALL" ? "blue" : "gray"}
            onClick={() => setStatusFilter("ALL")}
            className="unifiedResultsChip"
          >
            <FormattedMessage id="label.results.status.all" /> ({rows.length})
          </Tag>
          {statusOptions
            .filter((status) => statusCounts[status.id])
            .map((status) => (
              <Tag
                key={status.id}
                type={statusFilter === status.id ? "blue" : "gray"}
                onClick={() => setStatusFilter(status.id)}
                className="unifiedResultsChip"
              >
                {status.value} ({statusCounts[status.id]})
              </Tag>
            ))}
        </Column>

        {loadError && (
          <Column lg={16} md={8} sm={4}>
            {/* An empty table is how "nothing to do here" looks, so a load
                that failed has to say so itself — otherwise the two are the
                same picture and the work goes undone (OGC-1170). Same
                surface the stale-save rejection uses. */}
            <ActionableNotification
              kind="error"
              inline
              lowContrast
              hideCloseButton
              title={intl.formatMessage({ id: "error.results.loadFailed" })}
              subtitle={intl.formatMessage({
                id: "error.results.loadFailed.detail",
              })}
              actionButtonLabel={intl.formatMessage({
                id: "label.results.retry",
              })}
              onActionButtonClick={() => loadWorklist()}
              statusIconDescription={intl.formatMessage({
                id: "notification.title",
              })}
            />
          </Column>
        )}

        <Column lg={16} md={8} sm={4}>
          {arrows.show && <ServerPageArrows {...arrows} />}
          <TableContainer>
            {/* The expanded panel renders a second table (History) inside this
                one, so naming the outer table is what tells a screen-reader
                user which of the two they are in (OGC-1179). */}
            <Table
              size="lg"
              aria-label={intl.formatMessage({
                id: "label.results.table.caption",
              })}
            >
              <TableHead>
                <TableRow>
                  <TableHeader className="unifiedExpandHeader" />
                  <TableHeader>
                    {formatDomainMessage(intl, "label.results.subject", domain)}
                  </TableHeader>
                  <TableHeader>
                    <FormattedMessage id="label.results.test" />
                  </TableHeader>
                  <TableHeader>
                    <FormattedMessage id="label.results.method" />
                  </TableHeader>
                  <TableHeader>
                    <FormattedMessage id="label.results.analyzer" />
                  </TableHeader>
                  <TableHeader>
                    <FormattedMessage id="label.results.sample" />
                  </TableHeader>
                  <TableHeader>
                    {formatDomainMessage(intl, "label.results.range", domain)}
                  </TableHeader>
                  <TableHeader>
                    <FormattedMessage id="label.results.result" />
                  </TableHeader>
                  <TableHeader>
                    <FormattedMessage id="label.results.status" />
                  </TableHeader>
                  <TableHeader>
                    <FormattedMessage id="label.results.flag" />
                  </TableHeader>
                  <TableHeader>
                    <FormattedMessage id="label.results.actions" />
                  </TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row) => {
                  const key = worklistRowKey(row);
                  const state = rowStates[key] || "EMPTY";
                  const stale = staleInfo[key];
                  const reviewer = presence[row.analysisId];
                  const isExpanded = expandedRowKey === key;
                  const flag = resultFlagFor(row);
                  return (
                    <React.Fragment key={key}>
                      <TableRow>
                        <TableCell className="unifiedExpandCell">
                          <Button
                            kind="ghost"
                            size="sm"
                            aria-expanded={isExpanded}
                            aria-label={intl.formatMessage({
                              id: isExpanded
                                ? "label.results.collapseRow"
                                : "label.results.expandRow",
                            })}
                            className="unifiedExpandButton"
                            onClick={() =>
                              setExpandedRowKey(isExpanded ? null : key)
                            }
                          >
                            {isExpanded ? "▼" : "▶"}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {subjectCell(row)}
                          {reviewer && (
                            <Tag type="purple" className="unifiedResultsChip">
                              <FormattedMessage
                                id="label.results.inReviewBy"
                                values={{ 0: reviewer }}
                              />
                            </Tag>
                          )}
                        </TableCell>
                        <TableCell className="unifiedTestCell">
                          {row.testName}
                          {/* Whoever types in a value phoned through by the
                              reference laboratory reads this row, not the
                              expanded panel, so the tag belongs here too. */}
                          {row.referredOut && (
                            <Tag type="cyan" size="sm">
                              <FormattedMessage id="label.results.referredOut" />
                            </Tag>
                          )}
                        </TableCell>
                        <TableCell className="unifiedResultsSmallCell">
                          {methods.find((m) => m.id === row.testMethod)
                            ?.value ||
                            row.testMethod ||
                            "—"}
                          {loadedAnalyzers[key] && (
                            <Tag
                              type="cool-gray"
                              size="sm"
                              className="unifiedProvenance"
                            >
                              <FormattedMessage id="label.results.fromAnalyzer" />
                            </Tag>
                          )}
                        </TableCell>
                        <TableCell className="unifiedResultsSmallCell">
                          {analyzers.find((a) => a.id === row.analyzerId)
                            ?.value ||
                            row.analyzerId ||
                            "—"}
                        </TableCell>
                        <TableCell className="unifiedResultsSmallCell">
                          {row.sampleType || "—"}
                        </TableCell>
                        <TableCell>
                          {displayRange(intl, row)}{" "}
                          {row.unitsOfMeasure && !rangeNotAppliedKey(row)
                            ? row.unitsOfMeasure
                            : ""}
                        </TableCell>
                        <TableCell className="unifiedResultsValueCell">
                          <span className={accentClass(flag)}>
                            <PolymorphicResultCell
                              row={row}
                              editable={isRowEditable(state)}
                              onValueChange={(field, value) =>
                                handleValueChange(row, field, value)
                              }
                              onValueBlur={(nextFocus) =>
                                handleValueBlur(row, nextFocus)
                              }
                            />
                          </span>
                        </TableCell>
                        <TableCell>
                          {statusName(row.analysisStatusId) ? (
                            <Tag
                              size="sm"
                              type={statusTagType(row.analysisStatusId)}
                            >
                              {statusName(row.analysisStatusId)}
                            </Tag>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <FlagChip flag={flag} />
                        </TableCell>
                        <TableCell>
                          {showEdit(state) && (
                            <Button
                              kind="tertiary"
                              size="sm"
                              onClick={() => handleEdit(row)}
                            >
                              <FormattedMessage id="label.results.edit" />
                            </Button>
                          )}
                          {showSave(state) && (
                            <ESignatureButton
                              meaning={
                                isModifyingSavedResult(state)
                                  ? SignatureMeaning.MODIFIED
                                  : SignatureMeaning.AUTHORED
                              }
                              context={`${intl.formatMessage({
                                id: "label.results.save",
                              })} ${row.accessionNumber} - ${row.testName}`}
                              recordType="RESULT"
                              recordId={row.analysisId}
                              onBeforeSign={() => beforeSign(row)}
                              onSign={() => handleSave(row)}
                              size="sm"
                            >
                              <FormattedMessage id="label.results.save" />
                            </ESignatureButton>
                          )}
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow className="unifiedExpandedRow">
                          <TableCell colSpan={11}>
                            <ExpandedPanel
                              row={row}
                              domain={domain}
                              editable={isRowEditable(state)}
                              editing={isModifyingSavedResult(state)}
                              testSectionId={selectedLabUnit || undefined}
                              loadedAnalyzerId={loadedAnalyzers[key]}
                              methods={methods}
                              analyzers={analyzers}
                              noteDraft={
                                noteDrafts[key] || {
                                  text: "",
                                  visibility: "I",
                                }
                              }
                              dilutionDraft={
                                dilutionDrafts[key] || {
                                  measuredValue: "",
                                  factor: "",
                                }
                              }
                              sectionLayout={sectionLayout}
                              onSectionLayoutChange={setSectionLayout}
                              onFieldChange={(field, value) =>
                                handleFieldChange(row, field, value)
                              }
                              onValueChange={(field, value) =>
                                handleValueChange(row, field, value)
                              }
                              onNoteDraftChange={(draft) => {
                                setNoteDrafts((current) => ({
                                  ...current,
                                  [key]: draft,
                                }));
                                // A note is a change worth saving — the row it
                                // belongs to has to offer Save for it, now that
                                // opening Edit no longer does on its own.
                                markRowDirty(row);
                              }}
                              onDilutionDraftChange={(draft) => {
                                setDilutionDrafts((current) => ({
                                  ...current,
                                  [key]: draft,
                                }));
                                markRowDirty(row);
                              }}
                              allowResultRejection={allowResultRejection}
                              nceOpen={nceOpenKey === key}
                              onNceOpenChange={(open) => {
                                setNceOpenKey(open ? key : null);
                                setNceDisposition("NONE");
                                setNceRejectReasonId("");
                              }}
                              referralOrganizations={referralOrganizations}
                              referralReasons={referralReasons}
                              referralDraft={referralDrafts[key] || null}
                              onReferralDraftChange={(draft) =>
                                handleReferralDraftChange(row, draft)
                              }
                              referenceLabReportDate={
                                referenceLabReportDates[key] || ""
                              }
                              onReferenceLabReportDateChange={(value) =>
                                handleReferenceLabReportDateChange(row, value)
                              }
                              rejectReasons={rejectReasons}
                              rejectDraft={rejectDrafts[key] || null}
                              onRejectDraftChange={(draft) =>
                                handleRejectDraftChange(row, draft)
                              }
                              interpretationDraft={
                                interpretationDrafts[key] ?? null
                              }
                              onInterpretationDraftChange={(draft) =>
                                handleInterpretationDraftChange(row, draft)
                              }
                              nceDisposition={nceDisposition}
                              onNceDispositionChange={setNceDisposition}
                              nceRejectReasonId={nceRejectReasonId}
                              onNceRejectReasonChange={setNceRejectReasonId}
                              onNceApplyDisposition={(disposition, reasonId) =>
                                handleNceApplyDisposition(
                                  row,
                                  disposition,
                                  reasonId,
                                )
                              }
                              actions={
                                <>
                                  {showEdit(state) && (
                                    <Button
                                      kind="tertiary"
                                      size="sm"
                                      onClick={() => handleEdit(row)}
                                    >
                                      <FormattedMessage id="label.results.edit" />
                                    </Button>
                                  )}
                                  {showSave(state) && (
                                    <ESignatureButton
                                      meaning={
                                        isModifyingSavedResult(state)
                                          ? SignatureMeaning.MODIFIED
                                          : SignatureMeaning.AUTHORED
                                      }
                                      context={`${intl.formatMessage({
                                        id: "label.results.save",
                                      })} ${row.accessionNumber} - ${row.testName}`}
                                      recordType="RESULT"
                                      recordId={row.analysisId}
                                      onBeforeSign={() => beforeSign(row)}
                                      onSign={() => handleSave(row)}
                                      size="sm"
                                    >
                                      <FormattedMessage id="label.results.save" />
                                    </ESignatureButton>
                                  )}
                                </>
                              }
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      {stale && (
                        <TableRow>
                          <TableCell colSpan={11}>
                            {/* FR-O2 asks for a refresh ACTION, not only the
                                word: InlineNotification has no `actions` prop,
                                so the button handed to it was dropped and the
                                user was told to refresh with nothing to press
                                (OGC-1179). ActionableNotification is the
                                Carbon idiom, and it dismisses. */}
                            <ActionableNotification
                              kind="error"
                              inline
                              lowContrast
                              title={intl.formatMessage(
                                { id: "error.results.staleSave" },
                                {
                                  0:
                                    stale.modifiedBy ||
                                    intl.formatMessage({
                                      id: "label.results.anotherUser",
                                    }),
                                  1: stale.modifiedAt || "",
                                },
                              )}
                              actionButtonLabel={intl.formatMessage({
                                id: "label.results.refresh",
                              })}
                              onActionButtonClick={() => loadWorklist()}
                              onClose={() => {
                                setStaleInfo((current) => {
                                  const next = { ...current };
                                  delete next[key];
                                  return next;
                                });
                                return true;
                              }}
                              statusIconDescription={intl.formatMessage({
                                id: "notification.title",
                              })}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            {...serverPaginationProps({
              paging,
              rowsOnPage: filteredRows.length,
              pageSize: serverPageSize,
              onPageRequest: loadWorklistPage,
              intl,
            })}
          />
        </Column>
      </Grid>
      <ResultAlertModal
        open={Boolean(resultAlert)}
        alerts={resultAlert?.alerts || []}
        mode={resultAlert?.phase === "entry" ? "entry" : "save"}
        customCriticalMessage={resultAlert?.customCriticalMessage}
        onConfirm={confirmResultAlert}
        onCorrect={correctResultAlert}
      />
    </>
  );
};

export default UnifiedResults;
